import { AnalyticsItem, convertProductToAnalyticsItem } from './productsToAnalyticsItemConverter';
import { StateCart, StateCartEntry } from '../store/carts/CartsState';
import getPageType, { PageType } from '@spa/lib/tracking/getPageType';
import pushToDataLayer, {
  CartTypeQuantityData,
  PushEventType,
  TrackingEvent
} from './pushToDataLayer';
import { CustomOrderType } from '../store/carts/CustomOrderType';
import { getListContext } from './listContextStorage';

export enum CartActionType {
  ADD_TO_CART = 'add_to_cart',
  REMOVE_FROM_CART = 'remove_from_cart'
}

interface CartAdditionalData {
  readonly currency: string;
  readonly value: number;
  readonly searchMetadata: Record<string, string | undefined>;
  readonly page_type: string;
  readonly item_list_id?: string;
  readonly item_list_name?: string;
}

type AdditionalData = CartAdditionalData;
type AddToCartType = {
  readonly type: CartActionType.ADD_TO_CART;
};
type RemoveFromCartType = {
  readonly type: CartActionType.REMOVE_FROM_CART;
};
export type TrackCartActionsType = AddToCartType | RemoveFromCartType;

const buildEcommerceEvent = (
  eventType: TrackCartActionsType,
  analyticsItems: AnalyticsItem[],
  anonymousId: string | null,
  authenticatedId: string | null,
  additionalData: AdditionalData,
  cart?: CartTypeQuantityData | undefined
): PushEventType => ({
  type: TrackingEvent.ECOMMERCE,
  event: eventType.type,
  anonymousId,
  authenticatedId,
  ecommerce: {
    ...additionalData,
    items: analyticsItems
  },
  session: cart ? { cart } : undefined
});

const trackCartActions = (
  eventType: TrackCartActionsType,
  { entries, totalPrice, deliveryAddress }: StateCart,
  customOrderType: CustomOrderType,
  country: string,
  anonymousId: string | null,
  authenticatedId: string | null,
  pathname: string,
  searchMetadata: Record<string, string>,
  cartQuantityData?: CartTypeQuantityData | undefined
): void => {
  const cartDeliveryMethod = customOrderType === CustomOrderType.HOME_DELIVERY ? 'HD' : 'CC';
  const affiliation = deliveryAddress?.storeCode ?? `Online Shop ${country}`;
  const analyticsItems: Array<AnalyticsItem> = entries.map(
    (
      { product, quantity, tracking, totalPrice: { valueWithoutTax, currencyIso } }: StateCartEntry,
      index: number
    ) => {
      // Prefer the attribution captured at add-time (PDP passes it on the cart entry). For
      // non-PDP surfaces (PLP quick-add, cart line changes) it is absent, so fall back to the
      // list-context store keyed by product code (cross-tab, latest-selection-wins).
      const interactionOrigin =
        tracking?.itemListId || tracking?.itemListName
          ? { originId: tracking.itemListId ?? '', originType: tracking.itemListName ?? '' }
          : getListContext(product.code);
      const perUnitPrice = quantity > 0 ? Math.round((valueWithoutTax / quantity) * 100) / 100 : 0;
      return convertProductToAnalyticsItem(
        {
          ...product,
          quantity,
          campaignPrice: null,
          currentPrice: {
            valueWithoutTax: perUnitPrice,
            currencyIso
          }
        },
        index,
        interactionOrigin,
        cartDeliveryMethod,
        affiliation
      );
    }
  );
  const itemsValue: number = analyticsItems.reduce(
    (acc, item) => acc + (item.price ?? 0) * item.quantity,
    0
  );
  const pageType: PageType = getPageType({ pathname });
  // Event-level attribution (AC4): set only when every item shares one and the same list.
  const firstListId = analyticsItems[0]?.item_list_id;
  const firstListName = analyticsItems[0]?.item_list_name;
  const allItemsShareOneList: boolean =
    analyticsItems.length > 0 &&
    Boolean(firstListId) &&
    Boolean(firstListName) &&
    analyticsItems.every(
      (item) => item.item_list_id === firstListId && item.item_list_name === firstListName
    );
  const additionalData: AdditionalData = {
    currency: totalPrice.currencyIso,
    page_type: pageType,
    searchMetadata: { ...searchMetadata },
    value: itemsValue,
    ...(allItemsShareOneList ? { item_list_id: firstListId, item_list_name: firstListName } : {})
  };

  const purchaseEvent: PushEventType = buildEcommerceEvent(
    eventType,
    analyticsItems,
    anonymousId,
    authenticatedId,
    additionalData,
    cartQuantityData
  );

  pushToDataLayer(purchaseEvent);
};

export default trackCartActions;
